import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './Capabilities.css'



const API = process.env.REACT_APP_API_URL || "";

const CAPS = [
    {
        num: '01', tag: 'HARDWARE', accent: '#5d179e', rgb: '93,23,158',
        title: ['Drone', 'Sales'], sub: 'Products',
        desc: 'From consumer-grade platforms to enterprise-class fixed-wing systems — sourced, configured and delivered for your exact mission profile.',
        key: 'drone-sales', imgPos: 'center center', rotate: -3,
    },
    {
        num: '02', tag: 'INTEGRATION', accent: '#0ea5e9', rgb: '14,165,233',
        title: ['Components', '& Payloads'], sub: 'Accessories',
        desc: 'Precision gimbals, LiDAR modules, multispectral cameras, FPV kits — every component engineered to extend mission capability.',
        key: 'components', imgPos: 'center center', rotate: -1.5,
    },
    {
        num: '03', tag: 'GEO-INTEL', accent: '#2d6a4f', rgb: '45,106,79',
        title: ['Aerial', 'Mapping'], sub: 'Survey',
        desc: 'Survey-grade photogrammetric mapping with 2cm GSD accuracy. Orthomosaics, DEMs, point clouds — delivered mission-ready.',
        key: 'aerial-mapping', imgPos: 'center center', rotate: 0,
    },
    {
        num: '04', tag: 'INSPECTION', accent: '#b45309', rgb: '180,83,9',
        title: ['Industrial', 'Inspection'], sub: 'Monitoring',
        desc: 'Zero-access inspection of towers, tanks, solar arrays using thermal, RGB and zoom payloads. Safe. Fast. Documented.',
        key: 'inspection', imgPos: 'center 40%', rotate: 1.5,
    },
    {
        num: '05', tag: 'SUPPORT', accent: '#4a6fa5', rgb: '74,111,165',
        title: ['Support', '& Lifecycle'], sub: 'Maintenance',
        desc: 'Annual maintenance contracts, on-call technicians, firmware updates and end-to-end after-sales care across every product line.',
        key: 'support', imgPos: 'center 35%', rotate: 3,
    },
]

const SCROLL_PER = 500

export default function Capabilities() {
    const wrapRef = useRef(null)
    const deckRef = useRef(null)
    const cardRefs = useRef([])
    const [imgUrls, setImgUrls] = useState({})   // { key: url }
    const [activeIdx, setActiveIdx] = useState(0)

    // ── Fetch URLs from our API (MongoDB) ──────────────────────────────────────
    useEffect(() => {
        fetch(`${API}/api/media/capabilities`)
            .then(res => res.ok ? res.json() : {})
            .then(map => setImgUrls(map))   // map = { "drone-sales": "https://...", ... }
            .catch(() => { })
    }, [])

    // ── GSAP scroll animation (unchanged) ─────────────────────────────────────
    useEffect(() => {
        const cards = cardRefs.current
        const N = CAPS.length

        cards.forEach((card, i) => {
            if (!card) return
            gsap.set(card, {
                y: i === 0 ? 0 : '110vh',
                rotation: 0, scale: 1, zIndex: i,
                transformOrigin: 'center bottom',
            })
        })

        const ctx = gsap.context(() => {
            const master = gsap.timeline({
                scrollTrigger: {
                    trigger: wrapRef.current,
                    start: 'top top',
                    end: `+=${SCROLL_PER * (N - 1)}`,
                    scrub: true, anticipatePin: 1, pin: true, pinSpacing: true,
                    onUpdate(self) {
                        const idx = Math.min(N - 1, Math.floor(self.progress * N))
                        setActiveIdx(idx)
                    },
                },
            })

            for (let i = 1; i < N; i++) {
                const incoming = cards[i]
                for (let j = 0; j < i; j++) {
                    const prev = cards[j]
                    if (!prev) continue
                    const depth = i - j
                    master.to(prev, {
                        y: -(depth * 28),
                        rotation: CAPS[j].rotate * (0.4 + depth * 0.15),
                        scale: 1 - depth * 0.018,
                        zIndex: j, duration: 1, ease: 'none',
                    }, i - 1)
                }
                master.fromTo(incoming,
                    { y: '108vh', rotation: 0, scale: 1, zIndex: i },
                    { y: 0, rotation: 0, scale: 1, zIndex: i, duration: 1, ease: 'power1.out' },
                    i - 1
                )
                master.to({}, { duration: 0.15 }, `>`)
            }
        }, wrapRef)

        return () => ctx.revert()
    }, [])

    const active = CAPS[activeIdx]

    return (
        <section ref={wrapRef} className="cap-wrap">
            <div className="cap-bg" />
            <div className="cap-label">
                <span className="cap-label-dot" style={{ background: active.accent }} />
                CAPABILITIES
            </div>
            <div className="cap-counter">
                <span style={{ color: active.accent }}>{String(activeIdx + 1).padStart(2, '0')}</span>
                <span className="cap-counter-sep" />
                <span>{String(CAPS.length).padStart(2, '0')}</span>
            </div>
            <p className="cap-hint">scroll to stack</p>

            <div ref={deckRef} className="cap-deck">
                {CAPS.map((cap, i) => (
                    <div
                        key={cap.num}
                        ref={el => cardRefs.current[i] = el}
                        className="cap-card"
                        style={{ '--a': cap.accent, '--rgb': cap.rgb }}
                    >
                        <div className="cap-card-img">
                            {imgUrls[cap.key]
                                ? <img src={imgUrls[cap.key]} alt="" className="cap-card-photo"
                                    style={{ objectPosition: cap.imgPos }} draggable={false} />
                                : <div className="cap-card-photo-empty" />
                            }
                            <div className="cap-card-img-fade" />
                        </div>

                        <div className="cap-card-body">
                            <div className="cap-card-top">
                                <span className="cap-card-num">{cap.num}</span>
                                <span className="cap-card-tag">{cap.tag}</span>
                            </div>
                            <div className="cap-card-mid">
                                <span className="cap-card-sub">{cap.sub}</span>
                                <h2 className="cap-card-title">
                                    {cap.title.map((ln, li) => <span key={li}>{ln}</span>)}
                                </h2>
                                <div className="cap-card-rule" style={{ background: cap.accent }} />
                                <p className="cap-card-desc">{cap.desc}</p>
                            </div>
                            <div className="cap-card-bottom">
                                <div className="cap-card-stat">
                                    <span className="cap-card-stat-num" style={{ color: cap.accent }}>{cap.stat}</span>
                                    <span className="cap-card-stat-lbl">{cap.statLabel}</span>
                                </div>
                            </div>
                        </div>
                        <div className="cap-card-tab" style={{ background: cap.accent }} />
                    </div>
                ))}
            </div>
        </section>
    )
}